<template>
    <main>
        <!-- Hero section -->
        <section
            class="max-w-md mx-auto py-24 px-4 divide-y-2 divide-blue-gray-200 sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:py-32 lg:px-8"
            aria-labelledby="faq-heading"
        >
            <h2 id="heading" class="text-3xl font-extrabold text-blue-gray-900">
                <inertia-link href="/articles" class="text-indigo-500"> Artículos </inertia-link>
            </h2>
            <section aria-labelledby="officesHeading">
                <div class="max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
                    <h2 id="officesHeading" class="text-3xl font-extrabold text-warm-gray-900">
                        {{ article.title }}
                    </h2>
                    <div class="text-sm text-gray-500">Lectura en {{ article.time }} min.</div>
                    <p class="mt-6 text-lg text-warm-gray-500 max-w-3xl text-justify" v-html="article.body"></p>
                </div>
            </section>
        </section>

        <!-- This example requires Tailwind CSS v2.0+ -->
        <div class="bg-white">
            <div class="max-w-7xl mx-auto text-center py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
                <h2 class="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                    <span class="block">{{ $t('marketing.section8.cta_line1') }}</span>
                    <span class="block">{{ $t('marketing.section8.cta_line2') }}</span>
                </h2>
                <div class="mt-8 flex justify-center">
                    <div class="inline-flex rounded-md shadow">
                        <a
                            target="_blank"
                            :href="
                                `mailto:info@tichit.com?body=` +
                                $t('marketing.section8.mailto_body') +
                                `&subject=` +
                                $t('marketing.section8.mailto_subject')
                            "
                            class="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                            <svg
                                class="w-6 h-6 mr-1"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            {{ $t('marketing.section8.cta_contact_us') }}
                        </a>
                    </div>
                    <div class="ml-3 inline-flex">
                        <a
                            href="https://twitter.com/tichit_es"
                            class="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                        >
                            {{ $t('marketing.section8.cta_more_info') }}
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- Stats section -->
        <div class="relative bg-gray-900">
            <div class="h-80 absolute bottom-0 xl:inset-0 xl:h-auto xl:grid xl:grid-cols-2">
                <div class="h-full xl:relative xl:col-start-2">
                    <img
                        class="h-full w-full object-cover opacity-25 xl:absolute xl:inset-0"
                        src="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&ixlib=rb-1.2.1&auto=format&fit=crop&w=950&q=80&sat=-100"
                        alt="People working on laptops"
                    />
                    <div
                        aria-hidden="true"
                        class="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-gray-900 xl:inset-y-0 xl:left-0 xl:h-full xl:w-32 xl:bg-gradient-to-r"
                    />
                </div>
            </div>
            <div
                class="max-w-4xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8 xl:grid xl:grid-cols-2 xl:grid-flow-col-dense xl:gap-x-8"
            >
                <div class="relative pt-12 pb-64 sm:pt-24 sm:pb-64 xl:col-start-1 xl:pb-24">
                    <h2 class="text-sm font-semibold tracking-wide uppercase">
                        <span class="bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent">
                            {{ $t('marketing.section7.title') }}
                        </span>
                    </h2>
                    <p class="mt-3 text-3xl font-extrabold text-white">
                        {{ $t('marketing.section7.text') }}
                    </p>
                    <p class="mt-5 text-lg text-gray-300">
                        {{ $t('marketing.section7.text2') }}
                    </p>
                    <div class="mt-12 grid grid-cols-1 gap-y-12 gap-x-6 sm:grid-cols-2">
                        <p>
                            <span class="block text-2xl font-bold text-white">{{
                                $t('marketing.section7.metric1_title')
                            }}</span>
                            <span class="mt-1 block text-base text-gray-300">{{
                                $t('marketing.section7.metric1_text')
                            }}</span>
                        </p>

                        <p>
                            <span class="block text-2xl font-bold text-white">{{
                                $t('marketing.section7.metric2_title')
                            }}</span>
                            <span class="mt-1 block text-base text-gray-300">{{
                                $t('marketing.section7.metric2_text')
                            }}</span>
                        </p>

                        <p>
                            <span class="block text-2xl font-bold text-white">{{
                                $t('marketing.section7.metric3_title')
                            }}</span>
                            <span class="mt-1 block text-base text-gray-300">{{
                                $t('marketing.section7.metric3_text')
                            }}</span>
                        </p>

                        <p>
                            <span class="block text-2xl font-bold text-white">{{
                                $t('marketing.section7.metric4_title')
                            }}</span>
                            <span class="mt-1 block text-base text-gray-300">{{
                                $t('marketing.section7.metric4_text')
                            }}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import LandingLayout from '@/Shared/LandingLayout.vue';

export default {
    layout: LandingLayout,
    name: 'Show',
    props: {
        article: Object,
        meta: Object,
    },
    mounted() {
        this.$i18n.locale = this.meta.locale;
    },
};
</script>
